<template>
    <div class="w-full mx-auto h-full flex flex-col justify-center items-center">
        <div class="w-full max-w-md border border-gray-300 p-4">
            <div class="p-4 m-4 w-full flex flex-col items-center justify-center">
                <img src="@/assets/logo.png" alt="logo" class="w-18 h-18 mr-4">
                <span class="my-4 mx-2 text-2xl font-semibold">Colegio 5159</span>
            </div>

            <!-- Login -->
            <template v-if="viewMode === 'login'">
                <div class="w-full flex justify-start">Email</div>
                <input
                    v-model="formLogin.email"
                    type="email"
                    placeholder="Email"
                    class="w-full my-2 p-1 border border-gray-200 rounded-md"
                    :disabled="isLoading"
                    autocomplete="username"
                >
                <div class="w-full flex justify-start">Contraseña</div>
                <div class="relative w-full my-2">
                    <input
                        v-model="formLogin.password"
                        :type="showPassword ? 'text' : 'password'"
                        placeholder="Contraseña"
                        class="w-full p-1 pr-10 border border-gray-200 rounded-md"
                        :disabled="isLoading"
                        autocomplete="current-password"
                    >
                    <button
                        type="button"
                        class="absolute right-1 top-1/2 -translate-y-1/2 p-1.5 text-gray-500 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-400 rounded disabled:opacity-50"
                        :aria-label="showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'"
                        :disabled="isLoading"
                        @click="showPassword = !showPassword"
                    >
                        <font-awesome-icon :icon="['fas', showPassword ? 'eye-slash' : 'eye']" class="text-lg" />
                    </button>
                </div>
                <div class="text-red-400 text-sm" v-if="loginError">
                    {{ loginError }}
                </div>
                <div class="w-full flex justify-between items-center flex-wrap gap-2 my-1">
                    <router-link
                        :to="{ name: 'forgotPassword' }"
                        class="text-sm text-gray-600 hover:text-gray-900 underline"
                    >
                        ¿Olvidaste tu contraseña?
                    </router-link>
                    <button
                        type="button"
                        class="text-sm text-gray-600 hover:text-gray-900 underline"
                        :disabled="isLoading"
                        @click="goToRegisterEmail"
                    >
                        Registrarme como docente
                    </button>
                </div>
                <button
                    v-if="!isLoading"
                    type="button"
                    @click="tryLogin"
                    class="w-full my-2 px-4 py-2 text-gray-200 bg-gray-800 rounded-md hover:bg-gray-700 focus:outline-none focus:bg-gray-700">
                    Ingresar
                </button>
                <div v-else
                    class="flex items-center justify-center my-2 ">
                    <span class="h-6 w-6 animate-spin border-2 border-t-blue-500 border-transparent rounded-full"></span>
                </div>
            </template>

            <!-- Registro: paso email -->
            <template v-else-if="viewMode === 'register_email'">
                <p class="text-sm text-gray-600 mb-3">
                    Ingresá tu correo. Si no está registrado, podrás completar tus datos como docente.
                </p>
                <div class="w-full flex justify-start">Email</div>
                <input
                    v-model="registerEmailInput"
                    type="email"
                    placeholder="Email"
                    class="w-full my-2 p-1 border border-gray-200 rounded-md"
                    :disabled="isLoading"
                    autocomplete="email"
                    @keyup.enter="checkEmailAndContinue"
                >
                <div class="text-red-400 text-sm" v-if="registerEmailCheckError">
                    {{ registerEmailCheckError }}
                </div>
                <div class="w-full flex gap-2 mt-3">
                    <button
                        type="button"
                        class="flex-1 px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                        :disabled="isLoading"
                        @click="backToLogin"
                    >
                        Volver
                    </button>
                    <button
                        v-if="!isLoading"
                        type="button"
                        class="flex-1 px-4 py-2 text-gray-200 bg-gray-800 rounded-md hover:bg-gray-700"
                        @click="checkEmailAndContinue"
                    >
                        Continuar
                    </button>
                    <div v-else class="flex-1 flex items-center justify-center">
                        <span class="h-6 w-6 animate-spin border-2 border-t-blue-500 border-transparent rounded-full"></span>
                    </div>
                </div>
            </template>

            <!-- Registro: datos y contraseña -->
            <template v-else-if="viewMode === 'register_details'">
                <p class="text-sm text-gray-600 mb-3">
                    Completá tus datos. Se creará tu cuenta con rol docente.
                </p>
                <div class="w-full flex justify-start text-sm text-gray-500">Email</div>
                <div class="w-full my-1 p-1 border border-gray-100 bg-gray-50 rounded-md text-gray-800">
                    {{ formSignup.email }}
                </div>

                <div class="w-full flex justify-start mt-2">Contraseña</div>
                <div class="relative w-full my-2">
                    <input
                        v-model="formSignup.password"
                        :type="showSignupPassword ? 'text' : 'password'"
                        placeholder="Mínimo 8 caracteres"
                        class="w-full p-1 pr-10 border border-gray-200 rounded-md"
                        :disabled="isLoading"
                        autocomplete="new-password"
                    >
                    <button
                        type="button"
                        class="absolute right-1 top-1/2 -translate-y-1/2 p-1.5 text-gray-500 hover:text-gray-700 focus:outline-none rounded disabled:opacity-50"
                        :aria-label="showSignupPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'"
                        :disabled="isLoading"
                        @click="showSignupPassword = !showSignupPassword"
                    >
                        <font-awesome-icon :icon="['fas', showSignupPassword ? 'eye-slash' : 'eye']" class="text-lg" />
                    </button>
                </div>
                <div class="w-full flex justify-start">Repetir contraseña</div>
                <input
                    v-model="formSignup.confirmPassword"
                    :type="showSignupPassword ? 'text' : 'password'"
                    placeholder="Repetir contraseña"
                    class="w-full my-2 p-1 border border-gray-200 rounded-md"
                    :disabled="isLoading"
                    autocomplete="new-password"
                >

                <div class="w-full flex justify-start mt-2">Nombre</div>
                <input
                    v-model="formSignup.profile.firstname"
                    type="text"
                    class="w-full my-2 p-1 border border-gray-200 rounded-md"
                    :disabled="isLoading"
                >
                <div class="w-full flex justify-start">Apellido</div>
                <input
                    v-model="formSignup.profile.lastname"
                    type="text"
                    class="w-full my-2 p-1 border border-gray-200 rounded-md"
                    :disabled="isLoading"
                >
                <div class="w-full flex justify-start">DNI</div>
                <input
                    v-model="formSignup.profile.dni"
                    type="text"
                    class="w-full my-2 p-1 border border-gray-200 rounded-md"
                    :disabled="isLoading"
                >
                <div class="w-full flex justify-start">CUIL</div>
                <input
                    v-model="formSignup.profile.cuil"
                    type="text"
                    class="w-full my-2 p-1 border border-gray-200 rounded-md"
                    :disabled="isLoading"
                >
                <div class="w-full flex justify-start">Dirección</div>
                <input
                    v-model="formSignup.profile.address"
                    type="text"
                    class="w-full my-2 p-1 border border-gray-200 rounded-md"
                    :disabled="isLoading"
                >
                <div class="w-full flex justify-start">Teléfono</div>
                <input
                    v-model="formSignup.profile.phone"
                    type="text"
                    class="w-full my-2 p-1 border border-gray-200 rounded-md"
                    :disabled="isLoading"
                >

                <div class="text-red-400 text-sm" v-if="signupError">
                    {{ signupError }}
                </div>
                <div class="text-green-600 text-sm" v-if="signupSuccessMessage">
                    {{ signupSuccessMessage }}
                </div>

                <div class="w-full flex gap-2 mt-3">
                    <button
                        type="button"
                        class="flex-1 px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                        :disabled="isLoading"
                        @click="backToRegisterEmail"
                    >
                        Volver
                    </button>
                    <button
                        v-if="!isLoading && !signupSuccessMessage"
                        type="button"
                        class="flex-1 px-4 py-2 text-gray-200 bg-gray-800 rounded-md hover:bg-gray-700"
                        @click="submitSignup"
                    >
                        Crear cuenta
                    </button>
                    <div v-else-if="isLoading" class="flex-1 flex items-center justify-center">
                        <span class="h-6 w-6 animate-spin border-2 border-t-blue-500 border-transparent rounded-full"></span>
                    </div>
                    <button
                        v-if="signupSuccessMessage"
                        type="button"
                        class="flex-1 px-4 py-2 text-gray-200 bg-gray-800 rounded-md hover:bg-gray-700"
                        @click="backToLogin"
                    >
                        Ir al inicio de sesión
                    </button>
                </div>
            </template>
        </div>
    </div>
</template>
<script>
import { mapGetters } from 'vuex'
import { checkEmail, signup } from '@/service/user.service'

export default {
    name: "LoginForm",
    data() {
        return {
            viewMode: 'login',
            loginError: null,
            isLoading: false,
            showPassword: false,
            showSignupPassword: false,
            registerEmailInput: '',
            registerEmailCheckError: null,
            signupError: null,
            signupSuccessMessage: '',
            formLogin: {
                email: null,
                password: null,
            },
            formSignup: {
                email: '',
                password: '',
                confirmPassword: '',
                profile: {
                    firstname: '',
                    lastname: '',
                    dni: '',
                    cuil: '',
                    address: '',
                    phone: '',
                },
            },
        };
    },
    computed: {
        ...mapGetters('authentication', ['currentUser'])
    },
    methods: {
        goToRegisterEmail() {
            this.loginError = null
            this.registerEmailCheckError = null
            this.registerEmailInput = (this.formLogin.email || '').trim()
            this.viewMode = 'register_email'
        },
        backToLogin() {
            this.viewMode = 'login'
            this.registerEmailInput = ''
            this.registerEmailCheckError = null
            this.signupError = null
            this.signupSuccessMessage = ''
            this.resetSignupForm()
        },
        backToRegisterEmail() {
            this.viewMode = 'register_email'
            this.signupError = null
            this.signupSuccessMessage = ''
        },
        resetSignupForm() {
            this.formSignup = {
                email: '',
                password: '',
                confirmPassword: '',
                profile: {
                    firstname: '',
                    lastname: '',
                    dni: '',
                    cuil: '',
                    address: '',
                    phone: '',
                },
            }
        },
        async checkEmailAndContinue() {
            this.registerEmailCheckError = null
            const email = (this.registerEmailInput || '').trim()
            if (!email) {
                this.registerEmailCheckError = 'Ingresá un correo electrónico.'
                return
            }
            try {
                this.isLoading = true
                const { data } = await checkEmail({ email })
                if (data.exists) {
                    this.registerEmailCheckError = 'Este correo ya está registrado. Iniciá sesión o usá otro email.'
                    return
                }
                this.formSignup.email = email
                this.viewMode = 'register_details'
            } catch (error) {
                const msg = error.response?.data?.message || error.response?.data?.error
                this.registerEmailCheckError = typeof msg === 'string' ? msg : 'No se pudo verificar el correo. Intentá de nuevo.'
            } finally {
                this.isLoading = false
            }
        },
        buildSignupProfilePayload() {
            const p = this.formSignup.profile
            const profile = {
                firstname: (p.firstname || '').trim(),
                lastname: (p.lastname || '').trim(),
            }
            const optional = ['dni', 'cuil', 'address', 'phone']
            optional.forEach((key) => {
                const v = (p[key] || '').trim()
                if (v) profile[key] = v
            })
            return profile
        },
        async submitSignup() {
            this.signupError = null
            const email = (this.formSignup.email || '').trim()
            const password = this.formSignup.password || ''
            if (password.length < 8) {
                this.signupError = 'La contraseña debe tener al menos 8 caracteres.'
                return
            }
            if (password !== this.formSignup.confirmPassword) {
                this.signupError = 'Las contraseñas no coinciden.'
                return
            }
            const profile = this.buildSignupProfilePayload()
            if (!profile.firstname || !profile.lastname) {
                this.signupError = 'Nombre y apellido son obligatorios.'
                return
            }
            try {
                this.isLoading = true
                await signup({ email, password, profile })
                this.signupSuccessMessage = 'Cuenta creada. Ya podés iniciar sesión con tu correo y contraseña.'
            } catch (error) {
                const status = error.response?.status
                const data = error.response?.data
                if (status === 409) {
                    this.signupError = 'El correo ya está registrado.'
                } else if (status === 400 && data?.errors) {
                    const first = Array.isArray(data.errors) ? data.errors[0] : null
                    this.signupError = first?.message || 'Datos inválidos. Revisá el formulario.'
                } else {
                    const msg = data?.message || data?.error
                    this.signupError = typeof msg === 'string' ? msg : 'No se pudo crear la cuenta. Intentá de nuevo.'
                }
            } finally {
                this.isLoading = false
            }
        },
        async tryLogin() {
            try {
                this.loginError = null
                this.isLoading = true
                const response = await this.$store.dispatch('authentication/login', this.formLogin)
                console.log(response)
                if (response.status === 'error') {
                    this.loginError = response.message
                    this.isLoading = false
                } else {

                    const role = this.currentUser.role

                    switch (role) {
                        case "admin":
                            this.$router.push({ name: 'home' })
                            break;
                        case "administrative":
                            this.$router.push({ name: 'teachers' })
                            break;
                        case "executive":
                            this.$router.push({ name: 'teachers' })
                            break;
                        case "teacher":
                            this.$router.push({ name: 'home' })
                            break;
                        case "student_assistant":
                            this.$router.push({ name: 'home' })
                            break;
                        default:
                            this.$router.push({ name: 'home' })
                            break;
                    }


                }
            } catch (error) {
                console.log(error)
            }
        },
    }
};
 </script>

 <style scoped>
 </style>
